import { FILTER_OPERATORS } from '@smartsash/ui';
import type { FilterCondition, FilterOperator } from '@smartsash/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

/**
 * Every operator the builder can express, taken from the builder itself: the runtime guard below
 * and the type it narrows to now come from one tuple, so adding an operator cannot leave the
 * decoder behind.
 */
const OPERATORS: readonly string[] = FILTER_OPERATORS;

function isOperator(value: unknown): value is FilterOperator {
  return typeof value === 'string' && OPERATORS.includes(value);
}

/** The single search parameter the whole applied set lives in. */
const SEARCH_KEY = 'filters';

export interface TableFilterConditions {
  /** The conditions currently applied, read from the URL. */
  conditions: FilterCondition[];
  /** How many rows are applied — drives the applied-state dot on the trigger. */
  activeCount: number;
  /** Replace the applied set. Incomplete rows are dropped and the page returns to one. */
  apply: (next: FilterCondition[]) => void;
  /** Remove every condition, leaving the rest of the search alone. */
  clear: () => void;
}

/**
 * Keep only the rows that are complete enough to mean something.
 *
 * A `between` row keeps both slots even when one is empty, because the position is what says which
 * end of the range a date belongs to — compacting `['', '2026-01-01']` to `['2026-01-01']` would
 * turn an "until" bound into a "from" bound. Every other operator, `lte` included, drops its
 * blanks: a single-value operator has no position to preserve.
 */
function sanitize(next: FilterCondition[]): FilterCondition[] {
  return next
    .map((condition) =>
      condition.operator === 'between'
        ? { ...condition, values: [condition.values[0] ?? '', condition.values[1] ?? ''] }
        : { ...condition, values: condition.values.filter((value) => value !== '') },
    )
    .filter(
      (condition) => condition.field !== '' && condition.values.some((value) => value !== ''),
    );
}

/**
 * Read the applied set back out of the URL.
 *
 * The parameter is user-editable text, so every field is checked rather than cast: a hand-typed or
 * stale `?filters=` degrades to "nothing applied" instead of rendering a broken row.
 */
function decode(raw: unknown): FilterCondition[] {
  if (!Array.isArray(raw)) return [];
  const conditions: FilterCondition[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry == null) continue;
    const { field, operator, values } = entry as Record<string, unknown>;
    if (typeof field !== 'string' || field === '') continue;
    if (!isOperator(operator)) continue;
    if (!Array.isArray(values)) continue;
    const texts = values.filter((value): value is string => typeof value === 'string');
    conditions.push({ field, operator, values: texts });
  }
  return sanitize(conditions);
}

/**
 * Hold a list page's filter builder state in the URL (HS-1154 follow-up).
 *
 * The predecessor, `useListFilters`, kept one search parameter per field and therefore had to be
 * told which fields a list owned. The designed builder carries an operator and a multi-value
 * selection per row, which no longer fits a flat `?field=value` pair, so the whole set moves into
 * one `?filters=` parameter that the router serialises as JSON. Owning a single key also means an
 * unrelated parameter on the same route — `Laboratories` reads `?groupId=` — is untouched by
 * construction rather than by an ownership list.
 *
 * **Applying now narrows the rows on the server.** Until the filtering-foundation API landed the
 * builder was presentational, because no list endpoint accepted a filter beyond a scalar id. The
 * endpoints now take repeatable scope filters, date bounds and a free-text `search`, so a page
 * declares which of its builder fields reach which query parameter and `buildFilterParams` in
 * each app's own query-parameter mapper turns the applied set into that query. A field with no declared mapping
 * stays presentational rather than narrowing on the client, which would shrink the ten rows on
 * screen while looking like it shrank the result set.
 */
export function useTableFilterConditions(): TableFilterConditions {
  const navigate = useNavigate();
  // `strict: false` is the router's untyped escape hatch — this hook is route-agnostic by design,
  // so the value arrives as `unknown` and `decode` is what makes it safe to read.
  const search: unknown = useSearch({ strict: false });

  const conditions = useMemo(
    () => decode((search as Record<string, unknown> | null)?.[SEARCH_KEY]),
    [search],
  );

  const write = useCallback(
    (next: FilterCondition[]) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => {
          const { [SEARCH_KEY]: _cleared, ...carried } = prev;
          const updated: Record<string, unknown> = { ...carried };
          if (next.length > 0) updated[SEARCH_KEY] = next;
          // A filtered result set is shorter, so the page the user was on may not exist any more.
          updated.page = 1;
          return updated;
        },
      });
    },
    [navigate],
  );

  const apply = useCallback(
    (next: FilterCondition[]) => {
      write(sanitize(next));
    },
    [write],
  );

  const clear = useCallback(() => {
    write([]);
  }, [write]);

  return { conditions, activeCount: conditions.length, apply, clear };
}
